import React, { useCallback, useContext, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { OrderType } from '../../../interfaces/delivery-company/orders';
import axios, { AxiosResponse } from 'axios';
import {
  Alert,
  Box,
  Button,
  Grid,
  IconButton,
  keyframes,
  LinearProgress,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  alpha,
  Chip,
  Checkbox,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { AuthContext } from '../../../contexts/user-context/user-context';
import { API_ENDPOINT } from '../../../configurations/global.config';
import { motion, AnimatePresence } from 'framer-motion';
import { ErrorsFlattenner } from '../errors-flattenner/errors-flattenner';
import GenericTable from '../generic-table/generic-table';
import TextFieldWithScan from '../textfield-with-scan/textfield-with-scan';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle'; // Success icon
import { isMobile, isTablet } from 'react-device-detect';
import ReplayIcon from '@mui/icons-material/Replay';
import { ColumnConfig, GenericTableProps } from '../generic-table/utils';
import {
  decode,
  Decoding,
  DecodingWithOrderVal,
  same,
  transformOrdersToDecodingWithOrderVal,
} from '../../../utils/encoding';
import { emptyOrder } from '../../delivery-company-components/orders/create-order-form/create-order-form.utils';
import ConfirmAction from '../confirm-action/confirm-action';
import { useSnackBar } from '../../../contexts/snackbar';
import Grid2 from '@mui/material/Unstable_Grid2';
import { red } from '@mui/material/colors';
import { Id, ToastOptions } from 'react-toastify';

// Define keyframes for the animation
const bounceAnimation = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
`;

export interface timestampedMessage {
  content: string;
  level: 'info' | 'warning' | 'error' | 'success'
  timestamp: number;
}

export interface validationResult {
  validated: boolean;
  error?: string;
  /** If true, the scan requires user confirmation via a separate dialog. 
   *  The scanner will not show an error or add to failed scans. */
  pendingConfirmation?: boolean;
}

type scannedOrderValidator = (order: DecodingWithOrderVal) => validationResult;

function enrichWithPackageNumber<T>(cols: ColumnConfig<T>[]) {
  return [
    ...cols,
    {
      id: 'package',
      header: 'Package',
      display_body: (decoding: DecodingWithOrderVal) => (
        <>
          {decoding.current_package}/{decoding.number_of_packages}
        </>
      ),
    },
  ];
}

interface MultiPackageScannerProps {
  expectedOrders?: DecodingWithOrderVal[];
  validator: scannedOrderValidator;
  enforceAckOfFailedScans?: boolean;
  callback?: (
    orders: DecodingWithOrderVal[],
    setIsLoading: (isLoading: boolean) => void,
    setResponse: (resp?: AxiosResponse) => void,
    setMessage: (m: string) => void,
    config: any
  ) => Promise<void>;
  actionType?: string;
  getRenderOrdersProps: (
    orderDecoding: DecodingWithOrderVal[]
  ) => GenericTableProps<DecodingWithOrderVal>;
  disableCallbackOnNothingScanned?: boolean;
  onScanResultChanged?: (orders: DecodingWithOrderVal[]) => void;
  defaultScannedOrders?: DecodingWithOrderVal[];
  acceptTemporaryOrders?: boolean;
  allowUndoScan: boolean;
  disabled?: boolean;
  requirePendingBarcodes?: boolean; // useful for the case of order exchange
  commitFun?: (
    order: DecodingWithOrderVal,
    scannedPkgs: DecodingWithOrderVal[]
  ) => Promise<boolean>;
  rollbackFun?: (
    order: DecodingWithOrderVal,
    scannedPkgs: DecodingWithOrderVal[]
  ) => Promise<boolean>;
  label: string;
  withAccordionCollapse?: boolean;
  resetAllScan?: (scannedOrders: DecodingWithOrderVal[]) => Promise<boolean>;
  confirmationContent?: JSX.Element;
  confirmationContentSevereTargets?: { target: string; label: string }[];
  scannedOrdersTableHeader?: React.ReactNode;
  expectedOrdersTableHeader?: React.ReactNode;
}

interface asyncHandleScanResult {
  value?: DecodingWithOrderVal;
  error: string;
}

export interface MultiPackageScannerHandle {
  addOrder: (order: DecodingWithOrderVal) => void;
}

const MultiPackageScanner = forwardRef<MultiPackageScannerHandle, MultiPackageScannerProps>(function MultiPackageScanner({
  label,
  requirePendingBarcodes,
  withAccordionCollapse = true,
  expectedOrders,
  validator,
  enforceAckOfFailedScans = false,
  callback,
  actionType,
  getRenderOrdersProps,
  disableCallbackOnNothingScanned,
  onScanResultChanged,
  defaultScannedOrders,
  allowUndoScan,
  disabled,
  acceptTemporaryOrders,
  commitFun,
  rollbackFun,
  resetAllScan,
  confirmationContent,
  confirmationContentSevereTargets,
  scannedOrdersTableHeader,
  expectedOrdersTableHeader,
}, ref) {
  const [scannedOrders, setScannedOrders] = useState<DecodingWithOrderVal[]>(
    defaultScannedOrders ?? ([] as DecodingWithOrderVal[])
  );

  const [searchMode, setSearchMode] = useState<"id" | "ref">("id")

  const [scansBeingProcessed, setScansBeingProcessed] = useState<number>(0)

  const [failedScans, setFailedScans] = useState<string[]>([])
  const [failedScansAcknowledged, setFailedScansAcknowledged] = useState<boolean>(false)
  
  const addToScannedOrders = (newPackage: DecodingWithOrderVal) => {
    setScannedOrders((prevScannedOrders) =>
      prevScannedOrders.some((order) => same(order, newPackage))
        ? prevScannedOrders
        : [newPackage, ...prevScannedOrders]
    );
  };

  // Expose addOrder method via ref for external use
  useImperativeHandle(ref, () => ({
    addOrder: addToScannedOrders,
  }));

  const [expanded, setExpanded] = useState({
    toBeScanned: false,
    scannedOrders: false,
  });
  const [confirmActionDialogOpen, setConfirmActionDialogOpen] =
    useState<boolean>(false);
  const [resetScanDialogOpen, setResetScanDialogOpen] = React.useState(false);

  const handleAccordionChange =
    (panel: keyof typeof expanded) =>
    (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded({ ...expanded, [panel]: isExpanded });
    };

  const isExpectingMatches = expectedOrders != undefined;

  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingOrder, setIsFetchingOrder] = useState(false);
  const [response, setResponse] = useState<AxiosResponse>();
  const [message, setMessage] = useState('');
  const { showMessage : baseShowMessage } = useSnackBar();

  const showMessage = useCallback((message: string,
    level?: 'info' | 'error' | 'warning' | 'success',
    options?: { override?: boolean; id?: Id; toastOptions?: ToastOptions })=>{
      baseShowMessage(message, level, {...(options ?? {}), override: true, id: 'scan'})
  }, [baseShowMessage])

  const { user, config } = useContext(AuthContext);

  useEffect(() => {
    onScanResultChanged && onScanResultChanged(scannedOrders);
  }, [scannedOrders]);

  // Handle QR code input
  const handleScanByID = async (encoded_value: string): Promise<boolean> => {
    const asyncHandler = async (
      decoding: Decoding
    ): Promise<asyncHandleScanResult> => {
      if (decoding.type === 'error') {
        return { error: 'Unrecognized QRCode. Please Try Again.' };
      }

      // check if order is already scanned
      const alreadyScanned = scannedOrders.some((order) =>
        same(order, decoding)
      );
      if (alreadyScanned) {
        return { error: 'Already Scanned' };
      }

      if (decoding.type === 'temporary' && acceptTemporaryOrders) {
        return { value: { ...decoding, order: emptyOrder }, error: '' };
      }

      if (isExpectingMatches) {
        if (!toBeScanned) return { error: 'No Orders to Scan' }; // this should never happen

        // we should look for a matching package
        if (toBeScanned.length === 0)
          return { error: 'No More Orders To Scan' };

        const expectedMatchingPackage = toBeScanned.find((order) =>
          same(order, decoding)
        );
        if (!expectedMatchingPackage) return { error: 'Unexpected Package' };
        return { value: expectedMatchingPackage, error: '' }; // accepted the package
      } else {
        if (decoding.type === 'temporary') {
          if (!acceptTemporaryOrders) {
            return { error: 'Temporary QRCode not allowed.' };
          }
          return { value: { ...decoding, order: emptyOrder }, error: '' };
        } else {
          // we should fetch and accept if we can fetch it
          try {
            setIsFetchingOrder(true);
            const fetchedOrders = await axios.get<{
              results: OrderType[];
              count: number;
            }>(
              `${API_ENDPOINT}/api/v1/orders/?order_id__equals=${decoding.id}`,
              config
            );
            const scannedOrder = fetchedOrders.data.results[0];
            const toDecodings = transformOrdersToDecodingWithOrderVal([
              scannedOrder,
            ]);
            if (!toDecodings.some((correct) => same(correct, decoding))) {
              return { error: 'Wrong Package Details' };
            }
            const decodingWithVal: DecodingWithOrderVal = {
              ...decoding,
              order: scannedOrder,
            };
            return { value: decodingWithVal, error: '' };
          } catch (e) {
            return { error: 'Failed to find package details' };
          }
        }
      }
    };

    if (encoded_value.length === 0) return true;
    const decoding = decode(encoded_value);
    const { value, error } = await asyncHandler(decoding);

    //error occured
    if (error.length > 0 || !value) {
      showMessage(error.length > 0 ? error : 'Unexpected Error', 'error');
      return false;
    } else {
      if (decoding.type === 'temporary') {
        if (commitFun && !(await commitFun(value, scannedOrders))) {
          showMessage('Failed to save scan', 'error');
          return false;
        }
        addToScannedOrders(value);
        showMessage('Successfully Added');
        return true;
      }

      const validation = validator(value);
      
      // If pending confirmation, return success but don't add to scanned orders
      // The parent component handles this via a confirmation dialog
      if (validation.pendingConfirmation) {
        return true;
      }
      
      if (validation.error) {
        showMessage(validation.error, 'error');
        return false;
      }

      if (commitFun && !(await commitFun(value, scannedOrders))) {
        showMessage('Failed to save scan', 'error');
        return false;
      }

      showMessage('Successfully Added');
      addToScannedOrders(value);
    }
    setIsFetchingOrder(false);  
    return true;
  };

  const handleScanByReference = async (reference_id: string): Promise<boolean> => {
    const asyncHandler = async (ref_id: string) => {
      let fetchedOrders: OrderType[] = []
      try {
        setIsFetchingOrder(true)
        const fetchOrdersResponse = await axios.get<{
              results: OrderType[];
              count: number;
            }>(
              `${API_ENDPOINT}/api/v1/orders/?ref_number__equals=${ref_id}`,
              config
            );
         fetchedOrders = fetchOrdersResponse.data.results
      } catch {
          setIsFetchingOrder(false)
          return {error: 'Failed to get order.'}
      } finally {
        setIsFetchingOrder(false)
      }

      if (fetchedOrders.length == 0) return {error: 'Order not found.'}
      
      if (fetchedOrders.length > 1) {
        // multiple possible orders found
        return {error: 'Found multiple orders with the same reference. Use order id instead.'}
      }

      const order = fetchedOrders[0]

      if (order.number_of_packages > 1) {
        return {error: 'Order has more than one package. Scan each package separately.',}
      }

      // Order is possible good to go
      const decodingWithVal: DecodingWithOrderVal = transformOrdersToDecodingWithOrderVal([order])[0]
      return {value: decodingWithVal, error: ''}
    }

    if (reference_id.length === 0) return true;
    const { value, error } = await asyncHandler(reference_id);

    //error occured
    if (error.length > 0 || !value) {
      showMessage(error.length > 0 ? error : 'Unexpected Error', 'error');
      return false;
    } else {

      const validation = validator(value);
      
      // If pending confirmation, return success but don't add to scanned orders
      // The parent component handles this via a confirmation dialog
      if (validation.pendingConfirmation) {
        return true;
      }
      
      if (validation.error) {
        showMessage(validation.error, 'error');
        return false;
      }

      if (commitFun && !(await commitFun(value, scannedOrders))) {
        showMessage('Failed to save scan', 'error');
        return false;
      }

      showMessage('Successfully Added');
      addToScannedOrders(value);
    }
    setIsFetchingOrder(false);
    return true;
  };


  // Orders yet to be scanned
  // If no specific orders are expected, it is will be undefined
  const toBeScanned = expectedOrders
    ? expectedOrders.filter(
        (order) => !scannedOrders.some((scanned) => same(order, scanned))
      )
    : undefined;

  let toBeScannedTableRenderProps:
    | GenericTableProps<DecodingWithOrderVal>
    | undefined = undefined;
  if (toBeScanned) {
    toBeScannedTableRenderProps = getRenderOrdersProps(toBeScanned);
    const columns = enrichWithPackageNumber(
      toBeScannedTableRenderProps.columns
    );
    toBeScannedTableRenderProps = {
      ...toBeScannedTableRenderProps,
      columns: columns.map((item) => {
        return {
          ...item,
          display_body: (order) => (
            <motion.div
              key={order.id}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.3 }}
            >
              {item.display_body(order)}
            </motion.div>
          ),
        };
      }),
      items: toBeScannedTableRenderProps.items.sort((a, b) =>
        a.order.id < b.order.id ? -1 : 1
      ),
    };
  }

  let scannedOrdersTableProps: GenericTableProps<DecodingWithOrderVal> =
    getRenderOrdersProps(scannedOrders);
  let columns = enrichWithPackageNumber(scannedOrdersTableProps.columns);
  if (allowUndoScan) {
    columns = [
      ...columns,
      {
        id: 'remove',
        header: 'Undo',
        display_body: (order: DecodingWithOrderVal) => (
          <IconButton
            onClick={async () => {
              if (rollbackFun && !(await rollbackFun(order, scannedOrders))) {
                showMessage('Failed to unscan package', 'error');
                return;
              }
              setScannedOrders(scannedOrders.filter((x) => !same(x, order)));
              showMessage('Removed package');
            }}
          >
            <CancelIcon color="error"></CancelIcon>
          </IconButton>
        ),
      },
    ];
  }

  const expectedTotalPermanentPackages = expectedOrders
    ? expectedOrders.filter((item) => item.type === 'permanent').length
    : undefined;
  const remainingPackagesToBeScanned = toBeScanned?.length;
  const scannedPendingBarcodes = scannedOrders.filter(
    (item) => item.type === 'temporary'
  ).length;
  const scannedPermanentPackages =
    scannedOrders.length - scannedPendingBarcodes;

  scannedOrdersTableProps = {
    ...scannedOrdersTableProps,
    items: scannedOrders.sort((a, b) => (a.order.id < b.order.id ? -1 : 1)),
    columns: columns.map((item) => {
      return {
        ...item,
        display_body: (order) => (
          <motion.div
            key={order.id}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            {item.display_body(order)}
          </motion.div>
        ),
      };
    }),
  };
  const renderTableSection = (
    title: string,
    count: number | string,
    content: React.ReactNode,
    panelKey: keyof typeof expanded,
    header?: React.ReactNode
  ) => {
    if (!withAccordionCollapse) {
      return (
        <>
          <Typography variant="h6">
            {title} ({count})
          </Typography>
          {header && header}
          {content}
        </>
      );
    }

    return (
      <Accordion
        expanded={expanded[panelKey]}
        onChange={handleAccordionChange(panelKey)}
        sx={{
          width: '100%',
          boxShadow: 'none',
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          '&:before': {
            display: 'none',
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: expanded[panelKey] ? '#f5f5f5' : 'transparent',
            borderBottom: expanded[panelKey] ? '1px solid #e0e0e0' : 'none',
            minHeight: '48px !important',
            '& .MuiAccordionSummary-content': {
              margin: '12px 0',
            },
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            {title} ({count})
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: 0 }}>
          {header && header}
          {content}
        </AccordionDetails>
      </Accordion>
    );
  };

  const handleScan = async (value: string) => {
    setScansBeingProcessed((prev) => prev + 1)
    let success = false;
    try{
      if (searchMode == "id") {
        success = await handleScanByID(value)
      } else {
        success = await handleScanByReference(value)
      }
    } catch (e) {
      showMessage('An error occurred, please contact support.', 'error')
    } finally {
      if (!success) {
        setFailedScans((prev) => [...prev, value])
      }
      setScansBeingProcessed((prev) => prev - 1)
    }
    
  }

  return (
    <>
      {isLoading ? (
        <LinearProgress />
      ) : (
        <>
          {message != '' && (
            <>
              <Alert
                severity={
                  response && response.status >= 200 && response.status < 300
                    ? 'success'
                    : 'error'
                }
              >
                <>
                  {message}

                  {response && response.status >= 300 && (
                    <ErrorsFlattenner rawErrors={response.data} />
                  )}
                </>
              </Alert>
              <br />{' '}
            </>
          )}

          <div>
            <div style={{ margin: 'auto' }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Grid2 spacing={2} container sx={{width: "100%"}}>
                  <Grid2 xs={12} md={2}>
                      <FormControl key={'user-type-input'} fullWidth>
                  <InputLabel id="user-type-select-label"> Search By</InputLabel>
                  <Select label={"Search By"} value={searchMode} onChange={(e)=>setSearchMode(e.target.value as "id" | "ref")}>
                    <MenuItem value={"id"} key={"id"}>ID</MenuItem>
                    <MenuItem value={"ref"} key={"ref"}>Ref.</MenuItem>
                  </Select>
                  </FormControl>
                    
                  </Grid2>
                  <Grid2 xs={12} md={10}>
                      <TextFieldWithScan
                        label={label}
                        type="text"
                        variant="outlined"
                        disabled={disabled}
                        fullWidth
                        actOnEnterPressed
                        continuousScanning
                        clearOnAct
                        value={''}
                        act={handleScan}
                      />
                  </Grid2>
                </Grid2>
                
              
                

      
                
                {resetAllScan && (
                  <Button
                    variant="outlined"
                    aria-label="reset"
                    onClick={() => setResetScanDialogOpen(true)}
                    disabled={scannedOrders.length == 0}
                    sx={{
                      minWidth: '56px',
                      width: '56px',
                      height: '56px',
                      padding: 0,
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                      borderRadius: 1,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                    }}
                  >
                    <ReplayIcon />
                  </Button>
                )}
              </Box>
              {actionType && (
                <>
                  <br />
                    {failedScans.length > 0 && enforceAckOfFailedScans && (
                      <Alert 
                        severity="warning"
                        sx={{
                          maxWidth: '500px',
                          margin: 'auto',
                          mb: 2,
                        }}
                        
                        
                      >
                        <Typography
                        align='left'
                          sx={{
                            fontWeight: 600,
                            fontSize: '1rem',
                            letterSpacing: 0.2,
                            mb: 1,
                          }}
                        >
                          Failed to scan
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {failedScans.map((scan, idx) => (
                              <Chip
                                key={scan + idx}
                                label={scan}
                                onDelete={() => setFailedScans(failedScans.filter((value) => value !== scan))}
                                sx={{
                                  fontWeight: 600,
                                }}
                              />
                            ))}
                          </Box>

                          
                          <Typography
                            sx={{
                              mt: 1,
                              fontSize: '0.8rem',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                          >
                            <Checkbox
                            checked={failedScansAcknowledged}
                            onChange={(e) => setFailedScansAcknowledged(e.target.checked)}
                            
                            sx={{
                              mt: 1,
                              display: "inline-block",
                            }}
                          />
                            <span style={{display: 'inline-block'}}>I acknowledge to proceed despite the failed scans.</span>
                          </Typography>
                      </Alert>
                    )}
                  
                  {scansBeingProcessed > 0 && <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', margin: 1}}><CircularProgress size={20} color="primary"/> Processing... ({scansBeingProcessed} {scansBeingProcessed == 1 ? 'scan' : 'scans'} left)</Box>}
                  <div style={{ width: '100%', textAlign: 'center' }}>
                    <Button
                      size={(isMobile && !isTablet) ? 'small' : 'medium'}
                      variant="outlined"
                      color="primary"
                      disabled={
                        disabled ||
                        scansBeingProcessed > 0 ||
                        (failedScans.length > 0 && !failedScansAcknowledged && enforceAckOfFailedScans) ||
                        (toBeScanned != undefined && toBeScanned.length > 0) ||
                        (scannedOrders.length === 0 &&
                          disableCallbackOnNothingScanned) ||
                        (requirePendingBarcodes &&
                          scannedOrders.filter(
                            (item) => item.type === 'temporary'
                          ).length === 0)
                      }
                      onClick={(e) => {
                        if (confirmationContent) {
                          setConfirmActionDialogOpen(true);
                        } else {
                          e.preventDefault();
                          e.stopPropagation();
                          callback &&
                            callback(
                              scannedOrders,
                              (b) => setIsLoading(b),
                              (r) => setResponse(r),
                              (m) => setMessage(m),
                              config
                            );
                        }
                      }}
                    >
                      {actionType}
                    </Button>
                  </div>
                </>
              )}
            </div>

            <Grid
              container
              spacing={3}
              marginTop={1}
              style={{ textAlign: 'center' }}
            >
              {/* Table for orders yet to be scanned */}
              {toBeScanned != undefined && (
                <Grid item xs={12} md={6}>
                  {renderTableSection(
                    'To Be Scanned',
                    remainingPackagesToBeScanned || 0,
                    toBeScanned.length > 0 &&
                      toBeScannedTableRenderProps != undefined ? (
                      <GenericTable {...toBeScannedTableRenderProps} />
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          margin: 4,
                        }}
                      >
                        <CheckCircleIcon
                          sx={{
                            fontSize: 48,
                            color: 'success.main',
                            animation: `${bounceAnimation} 1s ease-in-out infinite`,
                          }}
                        />
                      </Box>
                    ),
                    'toBeScanned',
                    expectedOrdersTableHeader
                  )}
                </Grid>
              )}

              {/* Table for scanned orders */}
              <Grid item xs={12} md={toBeScanned == undefined ? 12 : 6}>
                {renderTableSection(
                  'Scanned Orders',
                  `${scannedPermanentPackages}${expectedOrders ? `/${expectedTotalPermanentPackages ? expectedTotalPermanentPackages : 0 + scannedPermanentPackages}` : ''}${scannedPendingBarcodes ? ' + ' + scannedPendingBarcodes : ''}`,
                  <AnimatePresence>
                    <GenericTable {...scannedOrdersTableProps} />
                  </AnimatePresence>,
                  'scannedOrders',
                  scannedOrdersTableHeader
                )}
              </Grid>
            </Grid>
          </div>

          {confirmActionDialogOpen && confirmationContent && (
            <ConfirmAction
              message="Please Confirm"
              action={async () => {
                callback &&
                  callback(
                    scannedOrders,
                    (b) => setIsLoading(b),
                    (r) => setResponse(r),
                    (m) => setMessage(m),
                    config
                  );
              }}
              isSevereAction={confirmationContentSevereTargets != undefined}
              confirmSevereActionTarget={
                confirmationContentSevereTargets
                  ? confirmationContentSevereTargets.map((item) => item.target)
                  : undefined
              }
              confirmSevereActionLabels={
                confirmationContentSevereTargets
                  ? confirmationContentSevereTargets.map((item) => item.label)
                  : undefined
              }
              isOpen={true}
              handleClose={() => setConfirmActionDialogOpen(false)}
            >
              <br />
              {confirmationContent}
            </ConfirmAction>
          )}

          <ConfirmAction
            message={'Are you sure you want to reset scanned packages?'}
            action={async () => {
              if (resetAllScan) {
                const resp = await resetAllScan(scannedOrders);

                if (resp) {
                  setScannedOrders([]);
                }
              }
            }}
            isOpen={resetScanDialogOpen}
            handleClose={() => setResetScanDialogOpen(false)}
          >
            <div> Attention: This action will reset all scanned packages!</div>
          </ConfirmAction>
        </>
      )}
    </>
  );
});

export default MultiPackageScanner;
